import type { ResponsiveBreakpoints, ResponsiveLayout, LayoutConfig } from '../schema';

// ============================================
// Default Breakpoints
// ============================================

/**
 * Default responsive breakpoints in pixels
 */
export const DEFAULT_BREAKPOINTS: Required<ResponsiveBreakpoints> = {
    mobile: 640,
    tablet: 768,
    desktop: 1024,
    large: 1280,
};

/**
 * Breakpoint names in order from smallest to largest
 */
export type BreakpointName = 'mobile' | 'tablet' | 'desktop' | 'large';

/**
 * Ordered list of breakpoint names
 */
export const BREAKPOINT_ORDER: BreakpointName[] = ['mobile', 'tablet', 'desktop', 'large'];

// ============================================
// Breakpoint Detection
// ============================================

/**
 * Get the current breakpoint based on window width
 */
export function getCurrentBreakpoint(
    width: number,
    breakpoints: ResponsiveBreakpoints = DEFAULT_BREAKPOINTS
): BreakpointName {
    const bp = { ...DEFAULT_BREAKPOINTS, ...breakpoints };

    if (width < bp.mobile) return 'mobile';
    if (width < bp.tablet) return 'tablet';
    if (width < bp.desktop) return 'desktop';
    return 'large';
}

/**
 * Check if width matches a specific breakpoint
 */
export function matchesBreakpoint(
    width: number,
    breakpoint: BreakpointName,
    breakpoints: ResponsiveBreakpoints = DEFAULT_BREAKPOINTS
): boolean {
    return getCurrentBreakpoint(width, breakpoints) === breakpoint;
}

/**
 * Check if width is at least a specific breakpoint
 */
export function isAtLeast(
    width: number,
    breakpoint: BreakpointName,
    breakpoints: ResponsiveBreakpoints = DEFAULT_BREAKPOINTS
): boolean {
    const bp = { ...DEFAULT_BREAKPOINTS, ...breakpoints };
    const thresholds: Record<BreakpointName, number> = {
        mobile: 0,
        tablet: bp.mobile,
        desktop: bp.tablet,
        large: bp.desktop,
    };
    return width >= thresholds[breakpoint];
}

// ============================================
// Responsive Layout Resolution
// ============================================

/**
 * Resolve the effective layout config for a given width
 */
export function resolveResponsiveLayout(
    responsiveLayout: ResponsiveLayout,
    width: number,
    breakpoints: ResponsiveBreakpoints = DEFAULT_BREAKPOINTS
): LayoutConfig {
    const breakpoint = getCurrentBreakpoint(width, breakpoints);
    const { base, mobile, tablet, desktop, large } = responsiveLayout;

    // Start with base layout
    let resolved: LayoutConfig = { ...base };

    // Apply overrides in order based on current breakpoint
    const breakpointIndex = BREAKPOINT_ORDER.indexOf(breakpoint);

    const overrides = [mobile, tablet, desktop, large];

    // Apply all overrides up to and including current breakpoint
    for (let i = 0; i <= breakpointIndex; i++) {
        const override = overrides[i];
        if (override) {
            resolved = { ...resolved, ...override };
        }
    }

    return resolved;
}

/**
 * Create media query string for a breakpoint
 */
export function createMediaQuery(
    breakpoint: BreakpointName,
    breakpoints: ResponsiveBreakpoints = DEFAULT_BREAKPOINTS
): string {
    const bp = { ...DEFAULT_BREAKPOINTS, ...breakpoints };

    switch (breakpoint) {
        case 'mobile':
            return `(max-width: ${bp.mobile - 1}px)`;
        case 'tablet':
            return `(min-width: ${bp.mobile}px) and (max-width: ${bp.tablet - 1}px)`;
        case 'desktop':
            return `(min-width: ${bp.tablet}px) and (max-width: ${bp.desktop - 1}px)`;
        case 'large':
            return `(min-width: ${bp.desktop}px)`;
    }
}

/**
 * Create min-width media query for breakpoint
 */
export function createMinWidthQuery(
    breakpoint: BreakpointName,
    breakpoints: ResponsiveBreakpoints = DEFAULT_BREAKPOINTS
): string {
    const bp = { ...DEFAULT_BREAKPOINTS, ...breakpoints };

    switch (breakpoint) {
        case 'mobile':
            return '(min-width: 0px)';
        case 'tablet':
            return `(min-width: ${bp.mobile}px)`;
        case 'desktop':
            return `(min-width: ${bp.tablet}px)`;
        case 'large':
            return `(min-width: ${bp.desktop}px)`;
    }
}

// ============================================
// Window Size Utilities
// ============================================

/**
 * Get current window width (SSR-safe)
 */
export function getWindowWidth(): number {
    if (typeof window === 'undefined') {
        return DEFAULT_BREAKPOINTS.desktop; // Default to desktop for SSR
    }
    return window.innerWidth;
}

/**
 * Check if running in browser
 */
export function isBrowser(): boolean {
    return typeof window !== 'undefined';
}

// ============================================
// CSS Utilities
// ============================================

/**
 * Convert a size value to CSS string
 */
export function toCssSize(value: string | number | undefined): string | undefined {
    if (value === undefined) return undefined;
    if (typeof value === 'number') return `${value}px`;
    return value;
}

/**
 * Generate responsive CSS custom properties
 */
export function generateResponsiveCssVars(
    breakpoints: ResponsiveBreakpoints = DEFAULT_BREAKPOINTS
): Record<string, string> {
    const bp = { ...DEFAULT_BREAKPOINTS, ...breakpoints };

    return {
        '--breakpoint-mobile': `${bp.mobile}px`,
        '--breakpoint-tablet': `${bp.tablet}px`,
        '--breakpoint-desktop': `${bp.desktop}px`,
        '--breakpoint-large': `${bp.large}px`,
    };
}
